import { Component, computed, input } from '@angular/core';

type IconColor = 'info' | 'success' | 'warning' | 'danger' | 'muted';

const DEFAULT_STATUS_MAP = {
    'in-bewerking': {
        label: 'In bewerking',
        icon: 'fa-square-pen',
        color: 'warning' as IconColor
    },
    ingediend: {
        label: 'Ingediend',
        icon: 'fa-circle-check',
        color: 'success' as IconColor
    },
    geblokkeerd: {
        label: 'Geblokkeerd',
        icon: 'fa-octagon-xmark',
        color: 'danger' as IconColor
    },
    'wijziging-in-bewerking': {
        label: 'Wijziging in bewerking',
        icon: 'fa-square-pen',
        color: 'warning' as IconColor
    },
    'wijziging-ingediend': {
        label: 'Wijziging ingediend',
        icon: 'fa-circle-check',
        color: 'success' as IconColor
    },
    'wijziging-geblokkeerd': {
        label: 'Wijziging geblokkeerd',
        icon: 'fa-octagon-xmark',
        color: 'danger' as IconColor
    }
} as const;

type DossierStatus = keyof typeof DEFAULT_STATUS_MAP;

@Component({
    selector: 'baas-dossier-status-badge',
    templateUrl: './baas-dossier-status-badge.html',
    host: { class: 'baas-dossier-status-badge' }
})
export class BaasDossierStatusBadge {
    // (string & {}) preserves autocomplete for known values while accepting arbitrary strings
    readonly status = input<DossierStatus | (string & {})>();
    readonly label = input<string>();
    readonly icon = input<string>();
    readonly iconColor = input<IconColor | undefined>();

    readonly #definedStatus = computed(() => {
        const status = this.status();
        return status ? DEFAULT_STATUS_MAP[status as keyof typeof DEFAULT_STATUS_MAP] : undefined;
    });

    protected readonly resolvedLabel = computed(() => this.label() ?? this.#definedStatus()?.label ?? '');

    protected readonly resolvedIconClasses = computed(() => {
        const icon = this.icon() ?? this.#definedStatus()?.icon;
        // Font Awesome icons are auto-prefixed with fa-solid if they're a single class name (no space).
        // Custom icon systems (multi-class or different prefix) should be passed with the full class string (includes space).
        // Examples: 'fa-banana' → 'fa-solid fa-banana', 'fa-light fa-banana' → 'fa-light fa-banana', 'lv-crop lv-crop-ajuin' → 'lv-crop lv-crop-ajuin'
        return icon?.startsWith('fa-') && !icon.includes(' ') ? `fa-solid ${icon}` : (icon ?? '');
    });

    protected readonly resolvedColor = computed(() => this.iconColor() ?? this.#definedStatus()?.color ?? 'muted');
}
